import { Settings2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";

interface Policy {
  id: string;
  project: string | null;
  entity_type: string | null;
  relation: string | null;
  thresholds: {
    markdown_write: number;
    render_link: number;
    graph_edge: number;
    related_widget: number;
  };
  status: "active" | "shadow";
}

const SAMPLE: Policy[] = [
  {
    id: "pol_global",
    project: null,
    entity_type: null,
    relation: null,
    thresholds: { markdown_write: 0.96, render_link: 0.9, graph_edge: 0.84, related_widget: 0.7 },
    status: "active",
  },
  {
    id: "pol_netbox",
    project: "netbox",
    entity_type: "doc",
    relation: "references",
    thresholds: { markdown_write: 0.94, render_link: 0.88, graph_edge: 0.8, related_widget: 0.65 },
    status: "active",
  },
  {
    id: "pol_platform_shadow",
    project: "platform",
    entity_type: null,
    relation: null,
    thresholds: { markdown_write: 0.92, render_link: 0.85, graph_edge: 0.78, related_widget: 0.62 },
    status: "shadow",
  },
];

const METRICS = [
  { label: "Enlaces activos (último 30d)", value: 1247 },
  { label: "Precisión @ render_link", value: "97.2%" },
  { label: "Click-through rate", value: "12.8%" },
  { label: "Feedback negativo", value: 18 },
  { label: "Restricted leakage", value: "0%" },
];

export function AutolinksTuning() {
  const [policies, setPolicies] = useState<Policy[]>(SAMPLE);

  function adjust(id: string, key: keyof Policy["thresholds"], delta: number) {
    setPolicies((prev) =>
      prev.map((p) =>
        p.id === id
          ? {
              ...p,
              thresholds: {
                ...p.thresholds,
                [key]: Math.max(0, Math.min(1, p.thresholds[key] + delta)),
              },
            }
          : p,
      ),
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Settings2 size={20} /> Admin · Autolinks
        </h1>
        <p className="text-sm text-(--text-secondary)">
          Calibración de umbrales y monitorización de precision/recall.
        </p>
      </header>

      <section className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        {METRICS.map((m) => (
          <div
            key={m.label}
            className="rounded-md border border-(--border-subtle) bg-(--bg-elevated) p-3"
          >
            <div className="text-2xl font-semibold">{m.value}</div>
            <div className="text-xs text-(--text-tertiary)">{m.label}</div>
          </div>
        ))}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Políticas</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-(--text-tertiary)">
              <tr className="border-b border-(--border-subtle)">
                <th className="py-2">Scope</th>
                <th>Status</th>
                <th>Markdown write</th>
                <th>Render link</th>
                <th>Graph edge</th>
                <th>Related widget</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr key={p.id} className="border-b border-(--border-subtle)">
                  <td className="py-2">
                    <div className="font-mono text-xs">{p.id}</div>
                    <div className="text-[10px] text-(--text-tertiary)">
                      {p.project ?? "global"} · {p.entity_type ?? "*"} · {p.relation ?? "*"}
                    </div>
                  </td>
                  <td>
                    <Badge variant={p.status === "active" ? "success" : "muted"}>{p.status}</Badge>
                  </td>
                  {(["markdown_write", "render_link", "graph_edge", "related_widget"] as const).map(
                    (k) => (
                      <td key={k}>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => adjust(p.id, k, -0.01)}
                            className="rounded border border-(--border-subtle) px-1 text-xs"
                          >
                            -
                          </button>
                          <span className="w-12 text-center font-mono text-xs">
                            {p.thresholds[k].toFixed(2)}
                          </span>
                          <button
                            type="button"
                            onClick={() => adjust(p.id, k, 0.01)}
                            className="rounded border border-(--border-subtle) px-1 text-xs"
                          >
                            +
                          </button>
                        </div>
                      </td>
                    ),
                  )}
                  <td>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toast.success(`Política ${p.id} guardada`)}
                    >
                      Guardar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
